import { useState, useRef, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import api from '@/lib/api';
import {
  MessageCircle, Loader2, ChevronDown, ChevronUp,
  Tractor, Laptop, Euro, Target, Lightbulb, 
  Sparkles, FileText, Copy, Users, BarChart3,
  ShieldCheck, Leaf, Package, Wrench, Clock, Award,
  TrendingUp, Zap, CheckCircle2, ArrowRight
} from 'lucide-react';

// ==================== AUDIT COMPLET BUREAU AGRICOLE TOUT EN UN ====================
const SECTIONS_AUDIT = [
  {
    id: 'profil',
    titre: 'Profil de l\'Exploitation',
    icon: Tractor,
    color: 'text-green-700',
    bgColor: 'bg-green-50',
    questions: [
      { id: 'type_exploitation', question: "Quel type d'exploitation avez-vous ?", aide: "Grandes cultures, viticulture, élevage bovin/ovin/caprin, maraîchage, arboriculture, polyculture-élevage, horticulture, apiculture...", type: 'select', options: ['Grandes cultures', 'Viticulture', 'Élevage bovin', 'Élevage ovin/caprin', 'Polyculture-élevage', 'Maraîchage', 'Arboriculture', 'Horticulture', 'Apiculture', 'Autre'], tags: ['parcellaire', 'stocks'] },
      { id: 'surface_sau', question: "Quelle est votre SAU (Surface Agricole Utile) ?", aide: "En hectares. Détermine la complexité de gestion parcellaire.", tags: ['parcellaire'] },
      { id: 'nb_parcelles', question: "Combien de parcelles exploitez-vous ?", aide: "Nombre approximatif. Plus il y en a, plus la cartographie est utile.", tags: ['parcellaire'] },
      { id: 'nb_salaries', question: "Combien de personnes travaillent sur l'exploitation ? (salariés, associés, saisonniers)", aide: "Détermine les besoins en gestion RH et le nombre de licences.", tags: ['rh'] },
      { id: 'nb_structures', question: "Combien de structures juridiques gérez-vous ?", aide: "EARL, GAEC, SCEA, SCI, individuel... Bureau Agricole gère le multi-sociétés.", tags: ['multi_societes'] },
      { id: 'ca_annuel', question: "Quel est votre chiffre d'affaires annuel approximatif ?", aide: "Permet de dimensionner la formule et d'estimer le ROI.", type: 'select', options: ['< 100 000 €', '100 000 - 300 000 €', '300 000 - 500 000 €', '500 000 - 1 000 000 €', '> 1 000 000 €'], tags: ['compta'] },
      { id: 'certifications', question: "Avez-vous des certifications ou labels ?", aide: "Bio, HVE, Demeter, AOP/AOC, Label Rouge, GlobalGAP, RSPO...", tags: ['certifications'] },
      { id: 'irrigation', question: "Pratiquez-vous l'irrigation ?", aide: "Si oui, gestion des tours d'eau, compteurs, coûts.", type: 'select', options: ['Oui, gravitaire', 'Oui, aspersion', 'Oui, goutte-à-goutte', 'Non', 'Projet d\'installation'], tags: ['parcellaire'] },
    ]
  },
  {
    id: 'outils',
    titre: 'Outils & Niveau Digital',
    icon: Laptop,
    color: 'text-indigo-700',
    bgColor: 'bg-indigo-50',
    questions: [
      { id: 'outil_actuel', question: "Quel(s) outil(s) utilisez-vous actuellement ?", aide: "Isagri, Smag Farmer, MesParcelles, Geofolia, Ekylibre, Excel, Papier...", tags: ['migration'] },
      { id: 'cout_outils', question: "Combien payez-vous par mois/an pour vos outils actuels ?", aide: "Isagri ~200-400€/mois, Smag ~150-300€. Comparaison directe.", tags: ['prix'] },
      { id: 'satisfaction_outil', question: "Sur 10, quelle note donnez-vous à votre outil actuel ?", aide: "< 5 = forte insatisfaction, 5-7 = améliorable, > 7 = satisfait.", type: 'select', options: ['1-2 (Très insatisfait)', '3-4 (Insatisfait)', '5-6 (Moyen)', '7-8 (Satisfait)', '9-10 (Très satisfait)'], tags: ['migration'] },
      { id: 'nb_outils', question: "Combien d'outils différents utilisez-vous au total ?", aide: "Compta + Parcellaire + RH + CRM = 4 outils. Bureau Agricole les unifie.", tags: ['tout_en_un'] },
      { id: 'double_saisie', question: "Faites-vous de la double saisie entre vos outils ?", aide: "Saisir les mêmes données dans 2+ logiciels = perte de temps.", type: 'select', options: ['Oui, souvent', 'Parfois', 'Rarement', 'Non'], tags: ['tout_en_un'] },
      { id: 'mobile', question: "Utilisez-vous un smartphone/tablette au champ ?", aide: "Bureau Agricole est 100% mobile-friendly.", type: 'select', options: ['Oui, quotidiennement', 'Parfois', 'Non, jamais'], tags: ['mobile'] },
    ]
  },
  {
    id: 'admin',
    titre: 'Gestion Administrative & Comptable',
    icon: Euro,
    color: 'text-blue-700',
    bgColor: 'bg-blue-50',
    questions: [
      { id: 'temps_admin', question: "Combien d'heures/semaine consacrez-vous à l'administratif ?", aide: "La moyenne est 8-12h/semaine. Bureau Agricole réduit de 60%.", tags: ['gain_temps'] },
      { id: 'expert_comptable', question: "Travaillez-vous avec un expert-comptable ?", aide: "Notre IA Pierre Moreau peut assister ou compléter son travail.", type: 'select', options: ['Oui, il fait tout', 'Oui, mais je prépare', 'Non, je fais seul', 'Non, conjoint/associé'], tags: ['compta'] },
      { id: 'regime_fiscal', question: "Quel est votre régime fiscal ?", aide: "Micro-BA, Réel simplifié, Réel normal. Pierre Moreau maîtrise tous les régimes.", type: 'select', options: ['Micro-BA', 'Réel simplifié', 'Réel normal', 'Je ne sais pas'], tags: ['compta'] },
      { id: 'tva', question: "Quel régime de TVA ?", aide: "RF (Remboursement Forfaitaire), RSA, RN. Alertes automatiques des échéances.", type: 'select', options: ['Remboursement Forfaitaire (RF)', 'RSA (Régime Simplifié)', 'Régime Normal', 'Je ne sais pas'], tags: ['compta'] },
      { id: 'pac', question: "Faites-vous votre déclaration PAC vous-même ?", aide: "Bureau Agricole aide à la préparation avec alertes calendrier.", type: 'select', options: ['Oui, seul', 'Avec l\'aide de la chambre', 'Mon comptable/conseiller la fait', 'Non concerné'], tags: ['pac'] },
      { id: 'msa', question: "Gérez-vous les appels MSA et cotisations vous-même ?", aide: "Alertes trimestrielles automatiques, calcul de provision.", type: 'select', options: ['Oui', 'Mon comptable', 'Je ne sais pas trop'], tags: ['compta'] },
      { id: 'facturation', question: "Éditez-vous des factures/devis pour vos clients ?", aide: "Facturation intégrée avec envoi automatique et suivi paiement.", type: 'select', options: ['Oui, beaucoup (>50/an)', 'Oui, quelques-unes', 'Non, vente en coopérative'], tags: ['crm'] },
      { id: 'tresorerie', question: "Suivez-vous votre trésorerie au jour le jour ?", aide: "Tableau de bord trésorerie en temps réel avec prévisionnel.", type: 'select', options: ['Oui, avec un outil', 'Oui, sur Excel', 'Non, je regarde mon compte en banque', 'Non, mon comptable s\'en charge'], tags: ['compta'] },
    ]
  },
  {
    id: 'technique',
    titre: 'Gestion Technique & Parcellaire',
    icon: Leaf,
    color: 'text-emerald-700',
    bgColor: 'bg-emerald-50',
    questions: [
      { id: 'assolement', question: "Comment planifiez-vous votre assolement / rotation ?", aide: "Outil de planification avec historique cultural et rotation automatique.", type: 'select', options: ['Sur papier/cahier', 'Sur Excel', 'Sur un logiciel dédié', 'De tête'], tags: ['parcellaire'] },
      { id: 'registre_phyto', question: "Comment tenez-vous votre registre phytosanitaire ?", aide: "Obligatoire légalement. Bureau Agricole l'auto-génère.", type: 'select', options: ['Papier', 'Excel', 'Logiciel (Isagri, Smag...)', 'Pas à jour...'], tags: ['parcellaire', 'reglementaire'] },
      { id: 'meteo', question: "Consultez-vous la météo pour planifier vos interventions ?", aide: "Météo intégrée dans Bureau Agricole avec alertes gel, pluie, vent.", type: 'select', options: ['Oui, c\'est essentiel', 'Parfois', 'Rarement'], tags: ['meteo'] },
      { id: 'gps_guidage', question: "Utilisez-vous le guidage GPS ou l'agriculture de précision ?", aide: "Intégration données capteurs, cartes de rendement, modulation.", type: 'select', options: ['Oui, guidage RTK', 'Oui, cartes de rendement', 'Non, pas encore', 'Projet'], tags: ['precision'] },
      { id: 'suivi_culture', question: "Comment suivez-vous l'état de vos cultures ?", aide: "Notes de terrain, photos géolocalisées, alertes sanitaires IA.", tags: ['parcellaire'] },
    ]
  },
  {
    id: 'commercial',
    titre: 'Vente & Relation Client',
    icon: TrendingUp,
    color: 'text-orange-700',
    bgColor: 'bg-orange-50',
    questions: [
      { id: 'vente_directe', question: "Faites-vous de la vente directe ?", aide: "CRM intégré, gestion fichier clients, fidélisation.", type: 'select', options: ['Oui, c\'est mon canal principal', 'Oui, en complément', 'Non, tout en coopérative/négoce', 'Projet de diversification'], tags: ['crm'] },
      { id: 'nb_clients', question: "Combien de clients avez-vous en vente directe ?", aide: "CRM avec fiches clients, historique, relances automatiques.", tags: ['crm'] },
      { id: 'site_web', question: "Avez-vous un site web ou une boutique en ligne ?", aide: "Studio IA intégré pour créer site + e-commerce.", type: 'select', options: ['Oui, site vitrine', 'Oui, avec e-commerce', 'Non, mais j\'aimerais', 'Non, pas besoin'], tags: ['ecommerce'] },
      { id: 'reseaux_sociaux', question: "Êtes-vous présent sur les réseaux sociaux ?", aide: "Module publication automatique Facebook/Instagram/LinkedIn.", type: 'select', options: ['Oui, actif', 'Oui, mais peu', 'Non'], tags: ['marketing'] },
    ]
  },
  {
    id: 'rh_equipe',
    titre: 'RH & Gestion d\'Équipe',
    icon: Users,
    color: 'text-violet-700',
    bgColor: 'bg-violet-50',
    questions: [
      { id: 'planning', question: "Comment gérez-vous le planning de vos salariés ?", aide: "Planning visuel, pointage, heures supplémentaires, congés.", type: 'select', options: ['Sur papier', 'Sur Excel', 'Logiciel RH', 'On s\'organise au jour le jour'], tags: ['rh'] },
      { id: 'saisonniers', question: "Employez-vous des saisonniers ?", aide: "Gestion contrats saisonniers, DPAE, documents fin de contrat.", type: 'select', options: ['Oui, beaucoup (>5)', 'Oui, quelques-uns', 'Non'], tags: ['rh'] },
      { id: 'paie', question: "Qui fait les bulletins de paie ?", aide: "Préparation paie intégrée avec export vers votre logiciel de paie.", type: 'select', options: ['Mon comptable', 'Un prestataire paie', 'Moi-même', 'Pas de salarié'], tags: ['rh'] },
      { id: 'securite', question: "Gérez-vous le Document Unique (DUERP) et la sécurité ?", aide: "Suivi EPI, formations, document unique avec alertes.", type: 'select', options: ['Oui, à jour', 'Oui, mais pas à jour', 'Non...', 'Je ne connais pas'], tags: ['rh', 'reglementaire'] },
    ]
  },
  {
    id: 'materiel',
    titre: 'Matériel & Logistique',
    icon: Wrench,
    color: 'text-gray-700',
    bgColor: 'bg-gray-100',
    questions: [
      { id: 'parc_materiel', question: "Combien de matériels motorisés possédez-vous ?", aide: "Tracteurs, moissonneuses, pulvérisateurs, etc.", type: 'select', options: ['1-3', '4-7', '8-15', '> 15'], tags: ['materiel'] },
      { id: 'suivi_materiel', question: "Comment suivez-vous l'entretien de votre matériel ?", aide: "Carnet d'entretien digital, alertes vidange, contrôle technique.", type: 'select', options: ['Carnet papier', 'Excel', 'De mémoire', 'Logiciel dédié'], tags: ['materiel'] },
      { id: 'couts_mecanisation', question: "Connaissez-vous vos coûts de mécanisation à l'hectare ?", aide: "Bureau Agricole calcule automatiquement les coûts par parcelle.", type: 'select', options: ['Oui, précisément', 'Approximativement', 'Non, aucune idée'], tags: ['materiel', 'compta'] },
      { id: 'carburant', question: "Suivez-vous votre consommation de carburant ?", aide: "Suivi GNR, cuve, bons de livraison, coût au litre.", type: 'select', options: ['Oui', 'Non'], tags: ['materiel'] },
    ]
  },
  {
    id: 'stocks',
    titre: 'Stocks & Traçabilité',
    icon: Package,
    color: 'text-amber-700',
    bgColor: 'bg-amber-50',
    questions: [
      { id: 'types_stocks', question: "Quels types de stocks gérez-vous ?", aide: "Phyto, semences, engrais, récoltes, aliments bétail, vin...", tags: ['stocks'] },
      { id: 'inventaire', question: "À quelle fréquence faites-vous un inventaire ?", aide: "Inventaire temps réel avec alertes seuils et réapprovisionnement.", type: 'select', options: ['Jamais', 'Une fois par an', 'Chaque trimestre', 'En continu'], tags: ['stocks'] },
      { id: 'tracabilite', question: "Avez-vous des obligations de traçabilité ?", aide: "Traçabilité lots, numéros de série, dates péremption.", type: 'select', options: ['Oui, forte (bio, AOP...)', 'Oui, standard', 'Non, pas vraiment'], tags: ['stocks', 'reglementaire'] },
      { id: 'pertes', question: "Avez-vous des problèmes de pertes / péremptions ?", aide: "Alertes automatiques de péremption et gestion FIFO.", type: 'select', options: ['Oui, régulièrement', 'Parfois', 'Non'], tags: ['stocks'] },
    ]
  },
  {
    id: 'decision',
    titre: 'Décision & Prochaines Étapes',
    icon: Award,
    color: 'text-rose-700',
    bgColor: 'bg-rose-50',
    questions: [
      { id: 'budget_mensuel', question: "Quel budget mensuel seriez-vous prêt à consacrer à un outil de gestion complet ?", aide: "Essentiel 49€, Pro 99€, Enterprise 199€. Économie vs outils actuels.", type: 'select', options: ['< 50 €/mois', '50-100 €/mois', '100-200 €/mois', '> 200 €/mois', 'Pas de budget défini'], tags: ['prix'] },
      { id: 'decideur', question: "Êtes-vous le décideur final pour ce type d'investissement ?", aide: "Si non, organiser une démo avec le(s) décideur(s).", type: 'select', options: ['Oui', 'Co-décision avec associé(s)', 'Non, il faut valider avec...'], tags: ['decision'] },
      { id: 'timing', question: "Quel serait le meilleur moment pour démarrer ?", aide: "Début de campagne (sept), après PAC (mai), changement d'exercice...", type: 'select', options: ['Immédiatement', 'Dans 1-2 mois', 'Prochain exercice', 'Je réfléchis encore'], tags: ['decision'] },
      { id: 'essai', question: "Un essai gratuit de 30 jours vous intéresserait ?", aide: "Taux de conversion après essai : 40%. Proposer systématiquement.", type: 'select', options: ['Oui, avec plaisir', 'Pourquoi pas', 'Non, je veux juste des infos', 'J\'ai déjà décidé'], tags: ['decision'] },
      { id: 'frein_principal', question: "Quel est votre principal frein au changement ?", aide: "Migration, formation, temps, habitudes, prix, confiance...", tags: ['objections'] },
      { id: 'priorite_1', question: "Si vous ne deviez résoudre qu'UN problème, lequel ?", aide: "La réponse oriente la démo et l'argumentaire personnalisé.", tags: ['priorite'] },
    ]
  },
];

// ==================== ARGUMENTS DYNAMIQUES ====================
const ARGUMENTS_CATALOGUE = {
  parcellaire: { titre: "Cartographie Intelligente", icon: "🗺️", detail: "Carte interactive de toutes vos parcelles, assolement, historique cultural sur 10 ans, rotation automatique. Photos géolocalisées au champ.", force: "Remplace MesParcelles + cahier de plaine" },
  compta: { titre: "IA Comptable Pierre Moreau", icon: "📊", detail: "Assistant IA spécialisé agriculture : TVA agricole (RF/RSA), MSA, micro-BA vs réel, alertes échéances, préparation clôture automatique.", force: "Réduit vos heures comptables de 60%" },
  stocks: { titre: "Gestion Stocks Temps Réel", icon: "📦", detail: "Phyto, semences, récoltes, cave : inventaire permanent, alertes seuils, péremptions, traçabilité lots, coûts par hectare.", force: "Fini les ruptures et les pertes" },
  rh: { titre: "Module RH Complet", icon: "👥", detail: "Planning visuel, pointage, heures supp, congés, saisonniers, DPAE auto, préparation paie, document unique DUERP.", force: "Gestion d'équipe professionnelle" },
  crm: { titre: "CRM & Facturation", icon: "💼", detail: "Fichier clients, devis/factures, relances auto, suivi paiements, historique achat, fidélisation. Stripe intégré.", force: "Vendez plus, gérez mieux" },
  materiel: { titre: "Parc Matériel Digital", icon: "🔧", detail: "Carnet d'entretien, alertes vidange/CT, coûts à l'hectare, suivi carburant GNR, heures moteur, amortissement.", force: "Maîtrisez vos coûts méca" },
  tout_en_un: { titre: "Tout-en-Un = Zéro Double Saisie", icon: "⚡", detail: "Un seul outil remplace 3-5 logiciels. Les données circulent entre modules. Saisie unique, vision globale.", force: "Gagnez 5-10h/semaine" },
  multi_societes: { titre: "Multi-Sociétés Natif", icon: "🏢", detail: "EARL + GAEC + SCI dans un seul compte. Comptabilité séparée, consolidation, transferts inter-sociétés.", force: "Impossible chez la concurrence" },
  prix: { titre: "Prix Imbattable", icon: "💰", detail: "Dès 49€/mois tout compris vs 200-400€ chez Isagri/Smag. Sans engagement. Migration gratuite de vos données.", force: "Divisez votre budget par 3" },
  migration: { titre: "Migration Gratuite & Accompagnée", icon: "🚀", detail: "On reprend vos données Isagri, Smag, MesParcelles, Geofolia gratuitement. Formation incluse. Support réactif.", force: "Zéro risque, zéro perte" },
  mobile: { titre: "100% Mobile", icon: "📱", detail: "Tout accessible depuis votre smartphone au champ. Mode hors-ligne, scan documents, photos géolocalisées.", force: "Votre bureau dans la poche" },
  reglementaire: { titre: "Conformité Réglementaire", icon: "✅", detail: "Registre phyto auto-généré, DUERP, traçabilité, facturation électronique 2026. Toujours aux normes.", force: "Plus jamais de stress contrôle" },
  pac: { titre: "Assistant PAC", icon: "🇪🇺", detail: "Alertes calendrier, préparation déclaration, calcul DPB/écorégimes, impact comptable des aides.", force: "PAC simplifiée" },
  meteo: { titre: "Météo Intégrée", icon: "🌤️", detail: "Prévisions 7 jours sur vos parcelles, alertes gel/grêle/vent, historique, aide décision interventions.", force: "Décisions éclairées" },
  certifications: { titre: "Suivi Certifications", icon: "🏅", detail: "Bio, HVE, AOP/AOC : cahier des charges, audits, documents, alertes renouvellement.", force: "Certifications sans stress" },
  ecommerce: { titre: "Site Web & E-Commerce", icon: "🌐", detail: "Créez votre site vitrine ou boutique en ligne avec l'IA. Référencement, paiement en ligne, livraison.", force: "Vendez en ligne facilement" },
  marketing: { titre: "Marketing Automatique", icon: "📣", detail: "Publication automatique sur Facebook/Instagram/LinkedIn. Campagnes email, newsletter, fidélisation.", force: "Visibilité sans effort" },
  gain_temps: { titre: "Gain de Temps Massif", icon: "⏰", detail: "Automatisation des tâches répétitives, IA qui travaille pour vous, rapports auto-générés, alertes proactives.", force: "Récupérez 5-10h/semaine" },
  precision: { titre: "Agriculture de Précision", icon: "🎯", detail: "Intégration données GPS, cartes de rendement, modulation, capteurs IoT. Analyse IA des données terrain.", force: "L'agriculture du futur" },
  objections: { titre: "Accompagnement Premium", icon: "🤝", detail: "Formation personnalisée, support réactif, webinaires mensuels, communauté d'agriculteurs utilisateurs.", force: "Vous n'êtes jamais seul" },
};

export default function AuditVenteTab() {
  const [notes, setNotes] = useState({});
  const [selectValues, setSelectValues] = useState({});
  const [checked, setChecked] = useState({});
  const [openSections, setOpenSections] = useState({ profil: true });
  const [aiSuggestion, setAiSuggestion] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [synthese, setSynthese] = useState('');
  const [syntheseLoading, setSyntheseLoading] = useState(false);
  const notesRef = useRef(notes);
  const selectRef = useRef(selectValues);
  notesRef.current = notes;
  selectRef.current = selectValues;

  const toggleSection = (id) => {
    setOpenSections(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const updateNote = (qId, value) => setNotes(prev => ({ ...prev, [qId]: value }));
  const updateSelect = (qId, value) => setSelectValues(prev => ({ ...prev, [qId]: value }));
  const toggleCheck = (qId) => setChecked(prev => ({ ...prev, [qId]: !prev[qId] }));

  const completedCount = Object.values(checked).filter(Boolean).length;
  const totalQuestions = SECTIONS_AUDIT.reduce((acc, s) => acc + s.questions.length, 0);

  // ==================== ARGUMENTS DYNAMIQUES ====================
  const dynamicArguments = useMemo(() => {
    const activeTags = new Set();
    
    // Collecter les tags des questions cochées
    SECTIONS_AUDIT.forEach(section => {
      section.questions.forEach(q => {
        if (checked[q.id] || notes[q.id]?.trim() || selectValues[q.id]) {
          (q.tags || []).forEach(tag => activeTags.add(tag));
        }
      });
    });

    // Logique conditionnelle
    const sv = selectValues;
    if (sv.nb_outils && sv.nb_outils !== '1') activeTags.add('tout_en_un');
    if (sv.double_saisie === 'Oui, souvent' || sv.double_saisie === 'Parfois') activeTags.add('tout_en_un');
    if (sv.satisfaction_outil?.includes('1-2') || sv.satisfaction_outil?.includes('3-4')) activeTags.add('migration');
    if (sv.temps_admin && notes.temps_admin) activeTags.add('gain_temps');
    if (sv.mobile === 'Oui, quotidiennement') activeTags.add('mobile');
    if (sv.registre_phyto === 'Pas à jour...' || sv.securite?.includes('pas à jour') || sv.securite?.includes('ne connais')) activeTags.add('reglementaire');
    if (sv.vente_directe?.includes('principal') || sv.vente_directe?.includes('complément')) activeTags.add('crm');
    if (sv.site_web?.includes('aimerais')) activeTags.add('ecommerce');
    if (sv.reseaux_sociaux?.includes('peu') || sv.reseaux_sociaux === 'Non') activeTags.add('marketing');
    if (sv.gps_guidage?.includes('RTK') || sv.gps_guidage?.includes('rendement')) activeTags.add('precision');
    if (notes.frein_principal?.trim()) activeTags.add('objections');

    // Toujours inclure prix
    activeTags.add('prix');

    // Construire la liste d'arguments triés par pertinence
    const args = [];
    activeTags.forEach(tag => {
      if (ARGUMENTS_CATALOGUE[tag]) {
        args.push({ ...ARGUMENTS_CATALOGUE[tag], tag });
      }
    });

    return args;
  }, [checked, notes, selectValues]);

  // Score de compatibilité
  const compatScore = useMemo(() => {
    if (completedCount < 5) return null;
    let score = 60; // Base
    const sv = selectValues;
    if (sv.satisfaction_outil?.includes('1-2') || sv.satisfaction_outil?.includes('3-4')) score += 15;
    if (sv.double_saisie === 'Oui, souvent') score += 10;
    if (sv.nb_structures && sv.nb_structures !== '1') score += 5;
    if (sv.temps_admin) score += 5;
    if (sv.essai?.includes('plaisir') || sv.essai?.includes('pourquoi pas')) score += 10;
    if (sv.timing === 'Immédiatement' || sv.timing === 'Dans 1-2 mois') score += 10;
    if (sv.budget_mensuel?.includes('100') || sv.budget_mensuel?.includes('200')) score += 5;
    return Math.min(score, 98);
  }, [completedCount, selectValues]);

  // Demander une suggestion IA
  const askAiSuggestion = async () => {
    setAiLoading(true);
    try {
      const allResponses = [];
      SECTIONS_AUDIT.forEach(s => s.questions.forEach(q => {
        const val = selectRef.current[q.id] || notesRef.current[q.id];
        if (val?.trim()) allResponses.push(`[${s.titre}] ${q.question}\n→ ${val}`);
      }));

      const res = await api.post('/brain/chat', {
        message: `Tu es Sophie, l'agent commercial de Bureau Agricole Tout en Un. Tu aides pendant un appel de vente. Voici les réponses déjà obtenues de ce prospect :\n\n${allResponses.join('\n\n') || "(Aucune réponse encore — on commence l'audit)"}\n\nAnalyse ce profil et :\n1. Suggère LA prochaine question la plus pertinente à poser maintenant\n2. Explique en 1 phrase pourquoi cette question est stratégique\n3. Donne l'argument de vente Bureau Agricole adapté à sa réponse probable\n4. Identifie le pain point principal détecté\n\nSois concise et percutante.`,
        agent_type: 'commercial'
      });
      setAiSuggestion(res.data.response);
    } catch (e) {
      toast.error("Erreur IA");
    } finally {
      setAiLoading(false);
    }
  };

  // Générer synthèse complète
  const generateSynthese = async () => {
    setSyntheseLoading(true);
    try {
      const allNotes = SECTIONS_AUDIT.flatMap(s =>
        s.questions.map(q => ({
          section: s.titre,
          question: q.question,
          reponse: selectRef.current[q.id] || notesRef.current[q.id] || '(non renseigné)'
        }))
      );
      const notesText = allNotes.map(n => `[${n.section}] ${n.question}\n→ ${n.reponse}`).join('\n\n');

      const res = await api.post('/brain/chat', {
        message: `Tu es Sophie, agent commercial senior de Bureau Agricole Tout en Un. Voici l'audit COMPLET d'un prospect :\n\n${notesText}\n\nGénère un rapport commercial structuré :\n\n## 1. Profil Prospect\nType d'exploitation, taille, complexité, niveau digital\n\n## 2. Pain Points Identifiés\nLes 3-5 problèmes principaux détectés\n\n## 3. Formule Recommandée\nEssentiel (49€), Pro (99€) ou Enterprise (199€) avec justification\n\n## 4. Modules Prioritaires\nLes modules qui répondent directement à ses besoins\n\n## 5. Arguments Personnalisés\nLes 5 arguments de vente les plus percutants pour CE prospect\n\n## 6. Objections Probables & Réponses\nAnticipe 3 objections et prépare les réponses\n\n## 7. ROI Estimé\nCalcul du temps gagné et des économies vs situation actuelle\n\n## 8. Plan d'Action\nProchaines étapes concrètes (démo, essai, formation, migration)\n\nSois spécifique et actionnable.`,
        agent_type: 'commercial'
      });
      setSynthese(res.data.response);
      toast.success('Synthèse commerciale générée !');
    } catch (e) {
      toast.error("Erreur génération synthèse");
    } finally {
      setSyntheseLoading(false);
    }
  };

  const copySynthese = () => {
    navigator.clipboard.writeText(synthese);
    toast.success('Copié dans le presse-papiers');
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
      {/* ==================== COLONNE GAUCHE : QUESTIONNAIRE ==================== */}
      <div className="lg:col-span-2 space-y-3">
        {/* Barre de progression */}
        <Card data-testid="audit-progress">
          <CardContent className="py-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium">Progression de l'audit</span>
              </div>
              <div className="flex items-center gap-2">
                {compatScore && (
                  <Badge variant="outline" className={compatScore > 80 ? 'border-green-500 text-green-700' : compatScore > 60 ? 'border-amber-500 text-amber-700' : 'border-gray-400'}>
                    Compatibilité : {compatScore}%
                  </Badge>
                )}
                <Badge variant={completedCount === totalQuestions ? 'default' : 'secondary'}>
                  {completedCount}/{totalQuestions}
                </Badge>
              </div>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-green-500 rounded-full h-2 transition-all duration-500"
                style={{ width: `${(completedCount / totalQuestions) * 100}%` }}
              />
            </div>
          </CardContent>
        </Card>

        {/* Sections de questions */}
        {SECTIONS_AUDIT.map(section => {
          const Icon = section.icon;
          const sectionChecked = section.questions.filter(q => checked[q.id]).length;
          const isOpen = openSections[section.id];
          return (
            <Card key={section.id} data-testid={`audit-section-${section.id}`} className={isOpen ? 'ring-1 ring-gray-200' : ''}>
              <CardHeader
                className="cursor-pointer hover:bg-muted/50 transition-colors py-3"
                onClick={() => toggleSection(section.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${section.bgColor}`}>
                      <Icon className={`w-4 h-4 ${section.color}`} />
                    </div>
                    <div>
                      <CardTitle className="text-sm">{section.titre}</CardTitle>
                      <span className="text-xs text-muted-foreground">{sectionChecked}/{section.questions.length}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {sectionChecked === section.questions.length && <CheckCircle2 className="w-4 h-4 text-green-500" />}
                    {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </div>
                </div>
              </CardHeader>
              {isOpen && (
                <CardContent className="space-y-3 pt-0">
                  {section.questions.map(q => (
                    <div key={q.id} className={`p-3 rounded-lg border transition-colors ${checked[q.id] ? 'bg-green-50/80 border-green-200' : 'bg-white border-gray-150'}`}>
                      <div className="flex items-start gap-3">
                        <Checkbox
                          checked={!!checked[q.id]}
                          onCheckedChange={() => toggleCheck(q.id)}
                          className="mt-0.5"
                          data-testid={`check-${q.id}`}
                        />
                        <div className="flex-1 space-y-2">
                          <p className="font-medium text-sm leading-snug">{q.question}</p>
                          <p className="text-xs text-muted-foreground">{q.aide}</p>
                          {q.type === 'select' ? (
                            <Select value={selectValues[q.id] || ''} onValueChange={val => updateSelect(q.id, val)}>
                              <SelectTrigger className="text-sm h-9" data-testid={`select-${q.id}`}>
                                <SelectValue placeholder="Choisir..." />
                              </SelectTrigger>
                              <SelectContent>
                                {q.options.map(opt => (
                                  <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Textarea
                              placeholder="Notes..."
                              value={notes[q.id] || ''}
                              onChange={e => updateNote(q.id, e.target.value)}
                              className="text-sm min-h-[50px] resize-none"
                              data-testid={`note-${q.id}`}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  {/* Auto-ouvrir la section suivante */}
                  {sectionChecked === section.questions.length && (
                    <div className="flex justify-end">
                      <Button variant="ghost" size="sm" className="text-xs gap-1" onClick={() => {
                        const idx = SECTIONS_AUDIT.findIndex(s => s.id === section.id);
                        if (idx < SECTIONS_AUDIT.length - 1) {
                          const next = SECTIONS_AUDIT[idx + 1].id;
                          setOpenSections(prev => ({ ...prev, [section.id]: false, [next]: true }));
                        }
                      }}>
                        Section suivante <ArrowRight className="w-3 h-3" />
                      </Button>
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>

      {/* ==================== COLONNE DROITE : IA + ARGUMENTS ==================== */}
      <div className="space-y-4">
        {/* Assistant IA */}
        <Card data-testid="ia-assistant-card" className="sticky top-4">
          <CardHeader className="py-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-amber-500" />
              Assistant IA Commercial
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button onClick={askAiSuggestion} disabled={aiLoading} className="w-full text-sm h-9" data-testid="ask-ia-btn">
              {aiLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MessageCircle className="w-4 h-4 mr-2" />}
              Quelle question poser maintenant ?
            </Button>
            <Button onClick={generateSynthese} disabled={syntheseLoading || completedCount < 5} variant="outline" className="w-full text-sm h-9" data-testid="generate-synthese-btn">
              {syntheseLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
              Générer rapport commercial
            </Button>
            {aiSuggestion && (
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 text-sm" data-testid="ia-suggestion">
                <ScrollArea className="max-h-[250px]">
                  <p className="whitespace-pre-wrap text-xs leading-relaxed">{aiSuggestion}</p>
                </ScrollArea>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Synthèse */}
        {synthese && (
          <Card data-testid="synthese-card">
            <CardHeader className="py-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm">Rapport Commercial</CardTitle>
                <Button variant="ghost" size="sm" onClick={copySynthese}><Copy className="w-3.5 h-3.5" /></Button>
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <div className="text-xs whitespace-pre-wrap leading-relaxed">{synthese}</div>
              </ScrollArea>
            </CardContent>
          </Card>
        )}

        {/* Arguments dynamiques */}
        <Card data-testid="arguments-card">
          <CardHeader className="py-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2">
                <Zap className="w-4 h-4 text-amber-500" />
                Arguments Adaptés
              </CardTitle>
              <Badge variant="outline" className="text-xs">{dynamicArguments.length}</Badge>
            </div>
            <p className="text-xs text-muted-foreground">
              {completedCount < 3 ? 'Remplissez l\'audit pour voir les arguments personnalisés' : 'Arguments basés sur les réponses du prospect'}
            </p>
          </CardHeader>
          <CardContent className="space-y-2">
            {dynamicArguments.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-4">
                Commencez l'audit pour générer des arguments ciblés
              </p>
            ) : (
              <ScrollArea className="max-h-[500px]">
                <div className="space-y-2 pr-2">
                  {dynamicArguments.map((arg, i) => (
                    <div key={i} className="p-2.5 rounded-lg bg-gradient-to-r from-white to-blue-50 border border-blue-100 space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="text-base">{arg.icon}</span>
                        <span className="font-semibold text-sm text-blue-900">{arg.titre}</span>
                      </div>
                      <p className="text-xs text-gray-700 leading-relaxed">{arg.detail}</p>
                      <div className="flex items-center gap-1.5 pt-0.5">
                        <ShieldCheck className="w-3 h-3 text-green-600" />
                        <span className="text-xs font-medium text-green-700">{arg.force}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>

        {/* Grille tarifaire */}
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Euro className="w-4 h-4 text-green-600" />
              Tarifs Bureau Agricole
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="p-2 rounded bg-green-50 border border-green-100 flex justify-between items-center">
              <div><span className="font-semibold">Essentiel</span><br /><span className="text-xs text-muted-foreground">Parcellaire + Compta IA + Stocks</span></div>
              <span className="font-bold text-lg text-green-700">49€</span>
            </div>
            <div className="p-2 rounded bg-blue-50 border border-blue-100 flex justify-between items-center">
              <div><span className="font-semibold">Pro</span><br /><span className="text-xs text-muted-foreground">+ CRM + RH + Marketing IA</span></div>
              <span className="font-bold text-lg text-blue-700">99€</span>
            </div>
            <div className="p-2 rounded bg-purple-50 border border-purple-100 flex justify-between items-center">
              <div><span className="font-semibold">Enterprise</span><br /><span className="text-xs text-muted-foreground">+ Multi-sociétés + Visio IA + API</span></div>
              <span className="font-bold text-lg text-purple-700">199€</span>
            </div>
            <p className="text-xs text-muted-foreground pt-1 border-t">Sans engagement | Migration gratuite | 30 jours d'essai</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
